import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type PolicyType = 'privacy' | 'terms' | 'refund';
interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: PolicyType;
}
const policyContent = {
  privacy: {
    title: 'Privacy Policy',
    content: <>
        <p className="mb-4" data-id="element-1140">
          <strong data-id="element-1141">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1142">
          Information We Collect
        </h3>
        <p className="mb-4" data-id="element-1143">
          We collect information you provide directly to us when you make a
          purchase, create an account, or communicate with us. This includes
          your name, email address, shipping address, payment information, and
          phone number.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1144">
          How We Use Your Information
        </h3>
        <p className="mb-4" data-id="element-1145">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-1146">
          <li data-id="element-1147">Process and fulfill your orders</li>
          <li data-id="element-1148">Send you order confirmations and shipping updates</li>
          <li data-id="element-1149">Respond to your customer service requests</li>
          <li data-id="element-1150">Send you marketing communications (with your consent)</li>
          <li data-id="element-1151">Improve our products and services</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1152">
          Information Sharing
        </h3>
        <p className="mb-4" data-id="element-1153">
          We do not sell or rent your personal information to third parties. We
          may share your information with:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-1154">
          <li data-id="element-1155">Service providers who help us operate our business</li>
          <li data-id="element-1156">Payment processors to complete transactions</li>
          <li data-id="element-1157">Shipping carriers to deliver your orders</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1158">
          Data Security
        </h3>
        <p className="mb-4" data-id="element-1159">
          We implement appropriate technical and organizational measures to
          protect your personal information against unauthorized access,
          alteration, disclosure, or destruction.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1160">
          Your Rights
        </h3>
        <p className="mb-4" data-id="element-1161">
          You have the right to access, correct, or delete your personal
          information. You may also opt out of marketing communications at any
          time.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1162">
          Contact Us
        </h3>
        <p className="mb-4" data-id="element-1163">
          If you have questions about this Privacy Policy, please contact us at
          privacy@ginext.com
        </p>
      </>
  },
  terms: {
    title: 'Terms of Service',
    content: <>
        <p className="mb-4" data-id="element-1164">
          <strong data-id="element-1165">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1166">
          Agreement to Terms
        </h3>
        <p className="mb-4" data-id="element-1167">
          By accessing or using GINEXT products and services, you agree to be
          bound by these Terms of Service. If you do not agree to these terms,
          please do not use our services.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1168">
          Product Information
        </h3>
        <p className="mb-4" data-id="element-1169">
          We strive to provide accurate product descriptions and pricing.
          However, we do not warrant that product descriptions, pricing, or
          other content is accurate, complete, reliable, current, or error-free.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1170">
          Orders and Payment
        </h3>
        <p className="mb-4" data-id="element-1171">
          By placing an order, you represent that you are legally capable of
          entering into binding contracts. All payments must be made in full at
          the time of purchase.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1172">
          Shipping and Delivery
        </h3>
        <p className="mb-4" data-id="element-1173">
          We ship to addresses within the United States. Shipping times may vary
          based on your location. Free shipping is available on orders over $50.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1174">
          Product Use
        </h3>
        <p className="mb-4" data-id="element-1175">
          GINEXT products are for external use only. Discontinue use if
          irritation occurs. These statements have not been evaluated by the
          FDA. This product is not intended to diagnose, treat, cure, or prevent
          any disease.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1176">
          Limitation of Liability
        </h3>
        <p className="mb-4" data-id="element-1177">
          GINEXT shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages resulting from your use of our
          products or services.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1178">
          Changes to Terms
        </h3>
        <p className="mb-4" data-id="element-1179">
          We reserve the right to modify these terms at any time. Continued use
          of our services after changes constitutes acceptance of the modified
          terms.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1180">
          Contact Information
        </h3>
        <p className="mb-4" data-id="element-1181">
          For questions about these Terms of Service, contact us at
          support@ginext.com
        </p>
      </>
  },
  refund: {
    title: '30-Day Refund Policy',
    content: <>
        <p className="mb-4" data-id="element-1182">
          <strong data-id="element-1183">Last Updated:</strong> December 22, 2025
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1184">
          Our Guarantee
        </h3>
        <p className="mb-4" data-id="element-1185">
          We stand behind the quality of GINEXT. If you're not completely
          satisfied with your purchase, we offer a 30-day money-back guarantee
          on your initial order.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1186">
          Eligibility
        </h3>
        <p className="mb-4" data-id="element-1187">To be eligible for a refund, you must:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-1188">
          <li data-id="element-1189">
            Request a refund within 30 days of your original purchase date
          </li>
          <li data-id="element-1190">
            Contact our customer service team to initiate the return process
          </li>
          <li data-id="element-1191">Provide your order number and reason for return</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1192">
          Refund Process
        </h3>
        <p className="mb-4" data-id="element-1193">Once we receive your refund request:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-1194">
          <li data-id="element-1195">
            We'll provide you with a return authorization and instructions
          </li>
          <li data-id="element-1196">You'll need to return the product (opened or unopened)</li>
          <li data-id="element-1197">
            Once we receive the return, we'll process your refund within 5-7
            business days
          </li>
          <li data-id="element-1198">Refunds will be issued to your original payment method</li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1199">
          Shipping Costs
        </h3>
        <p className="mb-4" data-id="element-1200">
          Original shipping costs are non-refundable. Return shipping costs are
          the responsibility of the customer unless the return is due to our
          error or a defective product.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1201">
          Exclusions
        </h3>
        <p className="mb-4" data-id="element-1202">The following are not eligible for refunds:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-1203">
          <li data-id="element-1204">Products purchased more than 30 days ago</li>
          <li data-id="element-1205">Products purchased from unauthorized retailers</li>
          <li data-id="element-1206">
            Products that have been tampered with or damaged due to misuse
          </li>
        </ul>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1207">Exchanges</h3>
        <p className="mb-4" data-id="element-1208">
          If you received a damaged or defective product, we'll replace it at no
          additional cost. Contact us immediately with photos of the damage.
        </p>

        <h3 className="text-xl font-bold text-charcoal mb-3 mt-6" data-id="element-1209">
          Contact Us
        </h3>
        <p className="mb-4" data-id="element-1210">
          To initiate a refund or if you have questions about our refund policy:
        </p>
        <ul className="list-none pl-0 mb-4 space-y-2" data-id="element-1211">
          <li data-id="element-1212">
            <strong data-id="element-1213">Email:</strong> support@ginextwarming.com
          </li>

          <li data-id="element-1214">
            <strong data-id="element-1215">Hours:</strong> Monday-Friday, 9am-5pm EST
          </li>
        </ul>

        <p className="mb-4 text-sm text-charcoal-light italic" data-id="element-1216">
          We're committed to your satisfaction. If you're not happy with GINEXT,
          we want to make it right.
        </p>
      </>
  }
};
export function PolicyModal({
  isOpen,
  onClose,
  type
}: PolicyModalProps) {
  const policy = policyContent[type];
  return <AnimatePresence data-id="element-1217">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" data-id="element-1218" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-1219">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden pointer-events-auto" data-id="element-1220">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-bone-dark px-6 py-4 flex items-center justify-between z-10" data-id="element-1221">
                <h2 className="text-2xl font-bold text-charcoal" data-id="element-1222">
                  {policy.title}
                </h2>
                <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-bone transition-colors flex items-center justify-center" data-id="element-1223">
                  <X className="w-5 h-5 text-charcoal" data-id="element-1224" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-1225">
                <div className="prose prose-sm max-w-none text-charcoal-light leading-relaxed" data-id="element-1226">
                  {policy.content}
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-bone border-t border-bone-dark px-6 py-4" data-id="element-1227">
                <button onClick={onClose} className="w-full bg-ginger text-white py-3 rounded-lg font-semibold hover:bg-ginger/90 transition-colors" data-id="element-1228">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}